/**
 * Senders picker block for CreativeColumn.
 * Supports gateway templates: save, apply/unlink, edit-in-place, delete.
 * For Media Devoted | Created By Ciro Autiero
 */

import type { FC } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { AnimatePresence } from 'framer-motion';
import { BookmarkPlus, Check, ChevronDown, ChevronRight, Plus, Search, Trash2 } from 'lucide-react';

import { Button } from '@/components/ui';
import { cn } from '@/lib/utils';
import { useAuth } from '@/providers/AuthProvider';
import { AiGenerateSendersDropdown } from '@/features/campaign-texts/components/AiGenerateSendersDropdown';
import { GeneratedSendersPreview } from '@/features/campaign-texts/components/GeneratedSendersPreview';
import { GeneratedSendersPreviewPerDrop } from '@/features/campaign-texts/components/GeneratedSendersPreviewPerDrop';
import { useAiSenderGeneration } from '@/features/smswiser-campaigns/components/ScaleDropModal/modals/scale-or-edit/creative/useAiSenderGeneration';
import {
  useCreateGatewaySenderTemplate,
  useDeleteGatewaySenderTemplate,
  useGatewaySenderTemplates,
  useUpdateGatewaySenderTemplate,
} from '@/hooks';
import { parseSenderNamesInput } from '../../shared/PrepareNewDropsForm.utils';

interface SendersPickerBlockProps {
  countryKey: string;
  routeId: number;
  senderNames: string[];
  selectedSenderNames: string[];
  senderSearch: string;
  setSenderSearch: (v: string) => void;
  showAddSender: boolean;
  setShowAddSender: (v: boolean | ((p: boolean) => boolean)) => void;
  newSenderValue: string;
  setNewSenderValue: (v: string) => void;
  onAddSender: (name: string) => Promise<void>;
  onAddSenders?: (senders: string[]) => Promise<void>;
  onDeleteSender: (name: string) => Promise<void>;
  onDeleteSenders?: (names: string[]) => Promise<void>;
  setSenderNames: (updater: (prev: string[]) => string[]) => void;
  setSelectedSenderNames: (updater: (prev: string[]) => string[]) => void;
  isBusy: boolean;
  onSaveGeneratedSendersAndSelectOnly?: (senders: string[]) => void;
  onPerDropSendersApplied?: (batches: string[][]) => void;
  onAiGeneratedSendersDeselectOld?: () => void;
  onMarkSendersForHide?: (senders: string[]) => void;
  pendingHideSenders?: string[];
  dropCount?: number;
  dropLabels?: Array<{ id: string; label: string }>;
  /** Called when AI senders preview is shown/hidden so parent can adjust layout. */
  onAiSendersActive?: (active: boolean) => void;
}

export const SendersPickerBlock: FC<SendersPickerBlockProps> = ({
  countryKey,
  routeId,
  senderNames,
  selectedSenderNames,
  senderSearch,
  setSenderSearch,
  showAddSender,
  setShowAddSender,
  newSenderValue,
  setNewSenderValue,
  onAddSender,
  onAddSenders,
  onDeleteSender,
  onDeleteSenders,
  setSenderNames,
  setSelectedSenderNames,
  isBusy,
  onSaveGeneratedSendersAndSelectOnly,
  onPerDropSendersApplied,
  onAiGeneratedSendersDeselectOld,
  onMarkSendersForHide,
  pendingHideSenders,
  dropCount = 1,
  dropLabels,
  onAiSendersActive,
}) => {
  const { hasPermission } = useAuth();
  const showAiSenders = hasPermission('campaign_senders.generate');

  const onAddSendersForAi = useCallback(
    async (senders: string[]) => {
      if (onAddSenders) await onAddSenders(senders);
      else for (const n of senders) await onAddSender(n);
    },
    [onAddSenders, onAddSender],
  );

  const onSavePerDropFlatten = useCallback(
    async (_batches: string[][]) => {
      // no-op: the hook's handleSavePerDrop/handleSaveAndHidePerDrop
      // already calls onAddSenders before this callback.
    },
    [],
  );

  const aiSenders = useAiSenderGeneration({
    countryKey,
    onAddSenders: onAddSendersForAi,
    onSaveGeneratedAndSelectOnly: onSaveGeneratedSendersAndSelectOnly,
    onMarkSendersForHide,
    onSaveGeneratedPerDrop: dropCount > 1 ? onSavePerDropFlatten : undefined,
    onPerDropSendersApplied,
    onAiGeneratedDeselectOld: onAiGeneratedSendersDeselectOld,
    onAfterSave: () => setShowAddSender(false),
  });

  const hasAiGenerated = Boolean(
    (aiSenders.perDropBatches && aiSenders.perDropBatches.length > 0) ||
      aiSenders.generatedSenders.length > 0,
  );
  useEffect(() => {
    onAiSendersActive?.(hasAiGenerated);
  }, [hasAiGenerated, onAiSendersActive]);
  useEffect(() => () => onAiSendersActive?.(false), [onAiSendersActive]);

  const [newSenderError, setNewSenderError] = useState<string | null>(null);
  const [showTemplates, setShowTemplates] = useState(true);
  const [activeTemplateId, setActiveTemplateId] = useState<string | null>(null);
  const lastSyncedRef = useRef<string>('');

  const {
    data: templates = [],
    isLoading: templatesLoading,
    isError: templatesError,
  } = useGatewaySenderTemplates(countryKey, routeId);
  const createTemplate = useCreateGatewaySenderTemplate(countryKey, routeId);
  const updateTemplate = useUpdateGatewaySenderTemplate(countryKey, routeId);
  const deleteTemplate = useDeleteGatewaySenderTemplate(countryKey, routeId);

  const handleTemplateClick = useCallback(
    async (t: { id: string; name: string; senderNames: string[] }) => {
      const names = t.senderNames as string[];
      if (activeTemplateId === t.id) {
        setActiveTemplateId(null);
        return;
      }
      const toAdd = names.filter((s) => !senderNames.includes(s));
      if (onAddSenders && toAdd.length > 0) {
        await onAddSenders(toAdd);
      } else if (toAdd.length > 0) {
        for (const s of toAdd) await onAddSender(s);
      }
      setSelectedSenderNames(() => names);
      setActiveTemplateId(t.id);
      lastSyncedRef.current = JSON.stringify([...names].sort());
    },
    [activeTemplateId, senderNames, onAddSenders, onAddSender, setSelectedSenderNames],
  );

  useEffect(() => {
    if (!activeTemplateId) return;
    const t = templates.find((x) => x.id === activeTemplateId);
    if (!t) {
      setActiveTemplateId(null);
      return;
    }
    if (selectedSenderNames.length === 0) return;
    const sorted = [...selectedSenderNames].sort();
    const key = JSON.stringify(sorted);
    if (key === lastSyncedRef.current) return;
    const tm = setTimeout(() => {
      const firstName = selectedSenderNames[0] ?? 'Template';
      const name =
        selectedSenderNames.length > 1
          ? `${firstName} +${selectedSenderNames.length - 1}`
          : firstName;
      updateTemplate.mutate({
        id: activeTemplateId,
        senderNames: selectedSenderNames,
        name,
      });
      lastSyncedRef.current = key;
    }, 400);
    return () => clearTimeout(tm);
  }, [activeTemplateId, selectedSenderNames, templates, updateTemplate]);

  const handleDeleteTemplate = useCallback(
    (e: React.MouseEvent, id: string) => {
      e.stopPropagation();
      deleteTemplate.mutate(id);
      if (activeTemplateId === id) setActiveTemplateId(null);
    },
    [activeTemplateId, deleteTemplate],
  );

  useEffect(() => {
    setActiveTemplateId(null);
  }, [countryKey, routeId]);

  const handleSaveAsTemplate = useCallback(async () => {
    if (selectedSenderNames.length === 0) return;
    const name = selectedSenderNames[0] ?? 'Template';
    await createTemplate.mutateAsync({
      name: selectedSenderNames.length > 1 ? `${name} +${selectedSenderNames.length - 1}` : name,
      senderNames: selectedSenderNames,
    });
    setShowTemplates(true);
  }, [selectedSenderNames, createTemplate]);
  const filtered = senderNames
    .filter((s) => s.toLowerCase().includes(senderSearch.toLowerCase()))
    .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));

  const applySenderInput = useCallback(
    async (value: string) => {
      const { senders, error } = parseSenderNamesInput(value);
      if (error) {
        setNewSenderError(error);
        return;
      }
      if (!senders.length) return;
      setNewSenderValue('');
      setNewSenderError(null);
      if (onAddSenders) {
        await onAddSenders(senders);
      } else {
        for (const s of senders) await onAddSender(s);
      }
      setShowAddSender(false);
    },
    [onAddSender, onAddSenders, setNewSenderValue, setShowAddSender],
  );

  const importFromPaste = useCallback(
    (pasted: string, e: React.ClipboardEvent) => {
      const { senders, error } = parseSenderNamesInput(pasted);
      if (error) {
        e.preventDefault();
        setNewSenderError(error);
        return;
      }
      if (!senders.length) return;
      e.preventDefault();
      setNewSenderValue('');
      setNewSenderError(null);
      if (onAddSenders) {
        onAddSenders(senders).then(() => setShowAddSender(false));
      } else {
        Promise.all(senders.map((s) => onAddSender(s))).then(() => setShowAddSender(false));
      }
    },
    [onAddSender, onAddSenders, setShowAddSender],
  );

  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-2">
      <div className="flex items-center justify-between">
        <label className="text-xs font-semibold text-muted-foreground">
          Campaign senders ({selectedSenderNames.length}/{senderNames.length} selected)
        </label>
        {routeId > 0 && selectedSenderNames.length > 0 && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-7 gap-1 text-xs"
            onClick={handleSaveAsTemplate}
            disabled={isBusy || createTemplate.isPending}
          >
            <BookmarkPlus className="h-3 w-3" /> Save as template
          </Button>
        )}
      </div>

      {routeId > 0 && (
        <div className="rounded-lg border border-border/60 bg-muted/5 p-2">
          <button
            type="button"
            onClick={() => setShowTemplates((v) => !v)}
            className="flex w-full items-center gap-2 text-left text-xs font-medium text-muted-foreground hover:text-foreground"
          >
            <ChevronRight className={cn('h-3.5 w-3.5 transition-transform', showTemplates && 'rotate-90')} />
            Gateway templates
            {templatesLoading
              ? ' (loading…)'
              : templatesError
                ? ' (unavailable)'
                : ` (${templates.length})`}
          </button>
          {showTemplates && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {templatesLoading ? (
                <span className="text-xs text-muted-foreground">Loading templates…</span>
              ) : templatesError ? (
                <span className="text-xs text-muted-foreground" role="status">
                  Unable to load templates. Try again later.
                </span>
              ) : templates.length === 0 ? (
                <span className="text-xs text-muted-foreground">No saved templates yet. Select senders and &quot;Save as template&quot;.</span>
              ) : (
                templates.map((t) => {
                  const isActive = activeTemplateId === t.id;
                  const names = t.senderNames as string[];
                  return (
                    <div key={t.id} className="inline-flex items-center gap-0.5">
                      <Button
                        type="button"
                        variant={isActive ? 'default' : 'ghost'}
                        size="sm"
                        className="h-7 gap-1 text-xs"
                        onClick={() => handleTemplateClick(t)}
                        disabled={isBusy}
                        title={
                          isActive
                            ? 'Click again to stop editing this template'
                            : `Apply: ${names.join(', ')}`
                        }
                      >
                        <BookmarkPlus className="h-3 w-3" />
                        {t.name}
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 shrink-0 p-0 text-destructive/70 hover:bg-destructive/10 hover:text-destructive"
                        onClick={(e) => handleDeleteTemplate(e, t.id)}
                        disabled={isBusy || deleteTemplate.isPending}
                        aria-label={`Delete template ${t.name}`}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  );
                })
              )}
            </div>
          )}
        </div>
      )}

      <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-md border border-border/60 bg-muted/5">
        <div className="flex flex-col gap-2 border-b border-border/60 px-2 py-2">
          <div className="relative min-w-0">
            <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <input
              className="h-8 w-full rounded-md border border-input bg-background pl-8 pr-2 text-xs outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring placeholder:text-muted-foreground"
              placeholder="Filter senders..."
              value={senderSearch}
              onChange={(e) => setSenderSearch(e.target.value)}
            />
          </div>
          <div className="flex flex-wrap items-center gap-1.5">
          {filtered.length > 0 && (
            <>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-7 shrink-0 text-xs"
                onClick={() => {
                  const allSelected = filtered.every((s) => selectedSenderNames.includes(s));
                  if (allSelected) {
                    setSelectedSenderNames((prev) => prev.filter((s) => !filtered.includes(s)));
                  } else {
                    setSelectedSenderNames((prev) =>
                      [...new Set([...prev, ...filtered])].filter((s) => senderNames.includes(s)),
                    );
                  }
                }}
                aria-label={
                  filtered.every((s) => selectedSenderNames.includes(s))
                    ? 'Deselect all filtered senders'
                    : 'Select all filtered senders'
                }
              >
                {filtered.every((s) => selectedSenderNames.includes(s)) ? 'Deselect all' : 'Select all'}
              </Button>
              {selectedSenderNames.length > 0 && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-7 w-7 shrink-0 p-0 text-destructive hover:bg-destructive/10 hover:text-destructive"
                    onClick={async (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    const toDelete = new Set(selectedSenderNames);
                    const names = [...toDelete];
                    if (onDeleteSenders) await onDeleteSenders(names);
                    else await Promise.all(names.map((name) => onDeleteSender(name)));
                    setSenderNames((prev) => prev.filter((v) => !toDelete.has(v)));
                    setSelectedSenderNames((prev) => prev.filter((v) => !toDelete.has(v)));
                  }}
                  disabled={isBusy}
                  aria-label={`Delete ${selectedSenderNames.length} selected sender(s)`}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              )}
            </>
          )}
          {showAiSenders && (
            <AiGenerateSendersDropdown
              countryKey={countryKey}
              dropCount={dropCount}
              dropLabels={dropLabels}
              onGenerated={aiSenders.handleAiGenerated}
            />
          )}
          {senderNames.length > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 gap-1 text-xs"
              onClick={() => setShowAddSender((v) => !v)}
              aria-expanded={showAddSender}
            >
              <ChevronDown className={cn('h-3.5 w-3.5 transition-transform', showAddSender && 'rotate-180')} />
              New sender
            </Button>
          )}
          </div>
        </div>

        {(() => {
          const hasAiGenerated = Boolean(
            (aiSenders.perDropBatches && aiSenders.perDropBatches.length > 0) ||
              aiSenders.generatedSenders.length > 0,
          );
          return (
            <>
              <div className={cn(hasAiGenerated && 'flex min-h-0 flex-1 flex-col overflow-hidden')}>
                <AnimatePresence>
                  {aiSenders.perDropBatches && aiSenders.perDropBatches.length > 0 ? (
                    <GeneratedSendersPreviewPerDrop
                      batches={aiSenders.perDropBatches}
                      groupIdsPerDrop={aiSenders.groupIdsPerDrop}
                      dropLabels={dropLabels}
                      onSave={aiSenders.handleSavePerDrop}
                      onDismiss={aiSenders.handleDismissPerDrop}
                      onRegenerate={aiSenders.handleRegeneratePerDrop}
                      onRegenerateAll={aiSenders.handleRegenerateAllPerDrop}
                      onSaveAndHide={aiSenders.handleSaveAndHidePerDrop}
                      isRegenerating={aiSenders.isRegenerating}
                      useAndHideOnly={dropCount > 1}
                      applied={aiSenders.perDropApplied}
                      generationTimeMs={aiSenders.perDropGenerationTimeMs}
                      fillHeight={hasAiGenerated}
                    />
                  ) : aiSenders.generatedSenders.length > 0 ? (
                    <GeneratedSendersPreview
                      senders={aiSenders.generatedSenders}
                      onSave={aiSenders.handleSaveGenerated}
                      onSaveAndHide={aiSenders.handleSaveAndHide}
                      onDismiss={aiSenders.handleDismiss}
                      onRegenerate={aiSenders.handleRegenerate}
                      isRegenerating={aiSenders.isRegenerating}
                      fillHeight={hasAiGenerated}
                    />
                  ) : null}
                </AnimatePresence>
              </div>
              {!hasAiGenerated && (showAddSender || senderNames.length === 0) && (
                <div className="border-b border-border p-2">
                  <textarea
                    className="min-h-[90px] w-full resize-none rounded-md border border-input bg-background p-2 text-xs outline-none placeholder:text-muted-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                    placeholder='Paste sender(s) or JSON (e.g. ["Sender A","Sender B"]) — JSON paste auto-adds'
                    value={newSenderValue}
                    onChange={(e) => {
                      setNewSenderValue(e.target.value);
                      setNewSenderError(null);
                    }}
                    onPaste={(e) => {
                      const pasted = e.clipboardData?.getData('text') ?? '';
                      if (!pasted.trim()) return;
                      const looksLikeJson = pasted.trimStart().startsWith('[') || pasted.trimStart().startsWith('{');
                      if (looksLikeJson) importFromPaste(pasted, e);
                    }}
                    disabled={isBusy}
                  />
                  {newSenderError ? (
                    <p className="mt-1.5 text-xs text-destructive" role="alert">
                      {newSenderError}
                    </p>
                  ) : null}
                  <div className="mt-2 flex items-center justify-end">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-7 gap-1 text-xs"
                      onClick={() => applySenderInput(newSenderValue)}
                      disabled={!newSenderValue.trim() || isBusy}
                    >
                      <Plus className="h-3 w-3" /> Add
                    </Button>
                  </div>
                </div>
              )}
            </>
          );
        })()}

        {!((aiSenders.perDropBatches && aiSenders.perDropBatches.length > 0) || aiSenders.generatedSenders.length > 0) && (
          <div className="flex min-h-0 min-w-0 flex-row flex-wrap content-start gap-1 overflow-x-hidden overflow-y-auto p-1.5 max-h-[120px]">
          {filtered.map((senderName, index) => {
            const isSelected = selectedSenderNames.includes(senderName);
            const isPendingHide = pendingHideSenders?.includes(senderName);
            return (
              <div
                key={`${senderName}-${index}`}
                data-testid="campaign-sender-item"
                data-index={index}
                role="button"
                tabIndex={0}
                aria-label={`${isSelected ? 'Deselect' : 'Select'} sender ${senderName}`}
                onClick={() =>
                  setSelectedSenderNames((prev) =>
                    prev.includes(senderName)
                      ? prev.filter((v) => v !== senderName)
                      : [...prev, senderName],
                  )
                }
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setSelectedSenderNames((prev) =>
                      prev.includes(senderName)
                        ? prev.filter((v) => v !== senderName)
                        : [...prev, senderName],
                    );
                  }
                }}
                className={cn(
                  'group inline-flex shrink-0 cursor-pointer items-center gap-1 rounded-md border px-2 py-0.5 transition-all',
                  isSelected
                    ? 'border-primary bg-primary/10 text-primary shadow-sm'
                    : 'border-border/60 bg-muted/40 hover:border-border hover:bg-muted/60',
                )}
              >
                <span
                  className={cn(
                    'flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded border transition-colors pointer-events-none',
                    isSelected
                      ? 'border-primary bg-primary text-primary-foreground'
                      : 'border-muted-foreground/60',
                  )}
                  aria-hidden
                >
                  {isSelected && <Check className="h-2 w-2 stroke-[2.5]" />}
                </span>
                <span
                  className="max-w-[200px] truncate text-sm font-medium"
                  title={senderName}
                  data-testid="campaign-sender-value"
                >
                  {senderName}
                </span>
                {isPendingHide && (
                  <span className="shrink-0 text-[9px] text-amber-600">Hides</span>
                )}
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 shrink-0 p-0 opacity-60 transition-opacity hover:opacity-100 hover:text-destructive group-hover:opacity-100"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDeleteSender(senderName).then(() => {
                      setSenderNames((prev) => prev.filter((v) => v !== senderName));
                      setSelectedSenderNames((prev) => prev.filter((v) => v !== senderName));
                    });
                  }}
                  disabled={isBusy}
                  aria-label={`Delete sender ${senderName}`}
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
              </div>
            );
          })}
          {filtered.length === 0 && (
            <div className="w-full py-6 text-center text-xs text-muted-foreground">
              {senderSearch ? 'No senders match your filter.' : 'No senders yet — add one above.'}
            </div>
          )}
          </div>
        )}
      </div>
    </div>
  );
};
